import { composeHeading, createStoreMethodologyFilter, defineDashboard, isKEur, resolveProduct } from '../../../_shared/dashboard-axes';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import {
  SortAlphabeticallyByColumnDataTransformer,
  SortByYearDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import {
  createIdNameTransformers,
  granularityHeadingPhrase,
  groupDetailOf,
} from '../../../../dashboard-config/granularities/granularity-profile';
import {
  DashboardForecastFromActivatedRouteSingleSelectFilter,
  DashboardStateSingleSelectFilter,
} from '../../../../filters/dashboard_filter_implementations';
import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { createTimeFilters, resolveTimeFilter } from '../../../_shared/time-filters';
import {
  createForecastValueChangeTransformers,
  FORECAST_METHODOLOGY_IDS,
  ForecastStructureMetricProfile,
} from '../forecast-metric.profile';
import { KPI_FILTER_NAME } from '../../../../dashboard-config/kpis/kpi-options';
import { DashboardForecastStructureDefinition } from './dashboard-forecast-structure.definition';
import {
  DashboardForecastStructureShell,
  dashboardForecastStructureShellProfiles,
} from './dashboard-forecast-structure-shell.profile';

export type CreateDashboardForecastStructureDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardForecastStructureShell,
    ForecastStructureMetricProfile
  >;

export function createDashboardForecastStructureDefinition(
  input: CreateDashboardForecastStructureDefinitionInput,
): DashboardForecastStructureDefinition {
  const { axes, shell, metric } = resolveProduct(input, dashboardForecastStructureShellProfiles);
  const granularityId = axes.granularity.id;
  const keys = metric.keys;
  // Forecast datasets are yearly, also for Employed.
  const time = resolveTimeFilter(axes, 'range', { yearly: true, defaultYearRange: 9 });

  return defineDashboard<DashboardForecastStructureDefinition>(axes, shell, {
    heading: (filterValues) =>
      composeHeading(
        ['PROGNOSE DER', metric.titleMetric(filterValues), granularityHeadingPhrase(granularityId, filterValues)],
        filterValues.get('years'),
      ),

    // Order matches legacy splices: years → KPI → granularity → state → methodology → forecast.
    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...createKpiFilters(axes.metric, granularityId),
      ...shell.buildGranularityFilters(deps),
      new DashboardStateSingleSelectFilter(deps.localDatasetsService, {}),
      createStoreMethodologyFilter(deps, { methodologies: FORECAST_METHODOLOGY_IDS, width: '250px' }),
      new DashboardForecastFromActivatedRouteSingleSelectFilter(deps.router, deps.localDatasetsService, {
        showInFilterComponent: false,
      }),
    ],

    preFilterTransformers: () => [new UndefinedStateToSumGermanyDashboardDataTransformer()],

    postFilterTransformers: (deps) => [
      ...createForecastValueChangeTransformers(keys, granularityId),
      new SortByYearDataTransformer(),
      ...createIdNameTransformers(deps, granularityId),
      new SortAlphabeticallyByColumnDataTransformer(shell.sortingKey),
    ],

    exportTransformers: (_deps) => metric.createExportTransformers(),

    view: {
      valueKey: keys.valueKey,
      valueChangeKey: keys.valueChangeKey,
      metricId: metric.metricId,
      groupDetail: groupDetailOf(granularityId, 'code'),
      kEur: isKEur(axes),
      metricFilterName: KPI_FILTER_NAME,
      legendMapKind: shell.legendMapKind,
    },
  });
}
